import { Component, OnDestroy, OnInit } from '@angular/core';
import { ActivatedRoute } from '@angular/router';
import { TranslateService } from '@ngx-translate/core';
import { AngularCsv } from 'angular-csv-ext/dist/Angular-csv';
import * as moment from 'moment';
import { Subscription } from 'rxjs';
import { CommonService } from 'src/app/shared/services/common.service';
import { SideMenuService } from 'src/app/shared/side-menu/side-menu.service';
import { SitesService } from 'src/app/sites/sites.service';

@Component({
	selector: 'app-daily-hourly-charger-usage-data',
	templateUrl: './daily-hourly-charger-usage-data.component.html',
	styleUrls: ['./daily-hourly-charger-usage-data.component.css']
})
export class DailyHourlyChargerUsageDataComponent implements OnInit, OnDestroy {
	siteSubscription: Subscription = new Subscription();

	siteId = 0;
	siteInfo: any = {};
	toDate = new Date((new Date()).getTime() - 24 * 60 * 60 * 1000); // yesterday
	fromDate = new Date((new Date()).getTime() - 30 * 24 * 60 * 60 * 1000); // 30 days ago
	minDate = this.fromDate;

	monthlyChartObject = {
		config:  {
			legend: 'none',
			bar: {
				groupWidth: 30
			},
			tooltip: {isHtml: true},
		},
		columnNames: ['Time', 'Data'],
		hasData: false
	}

	chartsData = {
		charge_hours: [],
		charge_ah: [],
		charge_kwh: [],
	};

	periodTypes = [
		{label: this.translate.instant('g.daily'), id: 'daily'},
		{label: this.translate.instant('g.hourly'), id: 'hourly'},
	];

	chartsList = [
		{id: 'all', label: this.translate.instant('g.all')},
		{id: 'charge_hours', label: this.translate.instant('fleet_reports.charge_hours')},
		{id: 'charge_ah', label: this.translate.instant('fleet_reports.charge_ah')},
		{id: 'charge_kwh', label: this.translate.instant('fleet_reports.charge_kwh')},
	];

	selectedCharts = 'all';
	selectedPeriodType = 'daily';

	get printFilters(): { label: string, value: string }[] {
		return [
			{ label: 'g.durations', value: this.commonService.getOptionLabel(this.periodTypes, this.selectedPeriodType) },
			{ label: 'perf_analytic.period', value: this.selectedPeriodType == 'hourly' ? moment(this.fromDate).format('MM/DD/YYYY') :
				`${moment(this.fromDate).format('MM/DD/YYYY')} - ${moment(this.toDate).format('MM/DD/YYYY')}` },
			{ label: 'g.charts', value: this.commonService.getOptionLabel(this.chartsList, this.selectedCharts) }
		];
	}

	constructor(
		private sitesService: SitesService,
		private route: ActivatedRoute,
		private translate: TranslateService,
		private sideMenuService: SideMenuService,
		private commonService: CommonService
	) {}

	ngOnInit() {
		this.siteId = this.route.parent.snapshot.params.siteId || 0;

		this.siteSubscription = this.sideMenuService.currentSite.subscribe((siteInfo:any) => {
			if (siteInfo && siteInfo.installation_date)
				this.minDate = moment.utc(siteInfo.installation_date).startOf('day').toDate();

			if (this.minDate > this.fromDate)
				this.fromDate = this.minDate;

			this.siteInfo = siteInfo;
			this.getDailyHourlyChargerUsageData();
		});
	}

	changePeriodType() {
		this.toDate = new Date((new Date()).getTime() - 24 * 60 * 60 * 1000); // today
		this.fromDate = new Date((new Date()).getTime() - 30 * 24 * 60 * 60 * 1000); // 30 days ago

		this.getDailyHourlyChargerUsageData();
	}

	changeDate(field, value) {
		this[field] = value;

		this.getDailyHourlyChargerUsageData();
	}

	getDailyHourlyChargerUsageData() {
		const fromDate = Math.floor(new Date(this.fromDate).getTime() / 1000);
		const toDate = Math.floor(new Date(new Date(this.toDate).setHours(23,59,59,999)).getTime() / 1000);

		const options = {
			from_date: fromDate,
			to_date: toDate,
			period_type: this.selectedPeriodType,
		};

		this.sitesService.getDailyHourlyChargerUsageData(this.siteId, options).subscribe((res: any) => {
			if (this.selectedPeriodType === 'daily')
				this.prepareDailyData(res);
			else
				this.prepareHourlyData(res);
		});
	}

	prepareDailyData(res) {
		this.chartsData.charge_hours = [];
		this.chartsData.charge_ah = [];
		this.chartsData.charge_kwh = [];

		for (let day in res) {
			this.chartsData.charge_hours.push([
				moment.unix(+day).format('YYYY-MM-DD'),
				res[day].charge_duration / 60 || 0,
			]);

			this.chartsData.charge_ah.push([
				moment.unix(+day).format('YYYY-MM-DD'),
				res[day].charge_as / 3600 || 0,
			]);

			this.chartsData.charge_kwh.push([
				moment.unix(+day).format('YYYY-MM-DD'),
				res[day].charge_ws / (3600 * 1000) || 0,
			]);
		}
	}

	prepareHourlyData(res) {
		this.chartsData.charge_hours = [];
		this.chartsData.charge_ah = [];
		this.chartsData.charge_kwh = [];

		for (let hour in res) {
			const formattedHour = this.formatHour(+hour);
			// hour 0 to 23
			this.chartsData.charge_hours.push([
				formattedHour,
				res[hour].charge_duration || 0,
			]);

			this.chartsData.charge_ah.push([
				formattedHour,
				res[hour].charge_as / 3600 || 0,
			]);

			this.chartsData.charge_kwh.push([
				formattedHour,
				res[hour].charge_ws / (3600 * 1000) || 0,
			]);
		}
	}

	formatHour(hour: number): string {
		const period = hour < 12 ? "AM" : "PM";
		let formattedHour = hour % 12;
		formattedHour = formattedHour === 0 ? 12 : formattedHour;
		return `${formattedHour}:00 ${period}`;
	}

	exportDataToCsv() {
		const csvFileHeader = this.commonService.getCsvFileHeadersInfo({
			customerName: this.siteInfo.customer_name,
			siteName: this.siteInfo.name
		});

		let csvData = [...csvFileHeader];
		for (let key in this.chartsData) {
			csvData.push([this.translate.instant('fleet_reports.' + key)]);
			csvData.push(['Time', 'Data']);
			csvData.push(...this.chartsData[key]);
			csvData.push([]);
		}
		new AngularCsv(csvData, this.translate.instant('perf_analytic.charge_opportunity_analysis'));
	}

	ngOnDestroy() {
		this.siteSubscription.unsubscribe();
	}
}
